import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Autocomplete, Box, TextField, Typography as T } from "@mui/material";

import {
  Label,
  LabelError,
  RequiredStar,
  ShadowPopper,
} from "components/atoms";
import { US_STATE_ABBREVIATIONS } from "helpers/usStates";
import { useStoreActions } from "store/store";
import { Country, Location, State } from "store/types";

import { VirtualListbox } from "./VirtualListbox";

type Props = {
  labelText: string;
  labelErrorText?: string;
  required?: boolean;
  disabled?: boolean;
  name: string;
  autocompleteData: State[] | Country[] | Location[] | null;
  additionalOnChange?: () => void;
  placeholder: string;
};

type NamedOption = {
  name: string;
  type?: string;
  is_airport?: string;
  is_areas?: string;
  location_id?: string;
  country_id?: string;
  state_id?: string;
};
type SelectOption = Location | State | Country;
type SelectOptionOrNull = SelectOption | null;

const hasName = (obj: unknown): obj is { name: string } =>
  typeof obj === "object" &&
  obj !== null &&
  typeof (obj as Record<string, unknown>).name === "string";

const getName = (obj: unknown): string => (hasName(obj) ? obj.name : "");

const AUTOFILL_WINDOW_MS = 1500;

const InputSelect = ({
  labelText,
  labelErrorText = "",
  required,
  name,
  autocompleteData,
  additionalOnChange,
  placeholder,
}: Props) => {
  const setSelectedCar = useStoreActions((a) => a.setSelectedCar);
  const { setValue } = useFormContext();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  // Holds an autofilled value (e.g. "MI") when it arrives BEFORE
  // autocompleteData has loaded — we retry the match in an effect that
  // watches autocompleteData, so the state field commits as soon as
  // `getStatesThunk` resolves. Without this, fast autofill on a fresh
  // step-5 mount leaves the input visually populated but the form value
  // empty, and submit fails with "field required".
  const [pendingAutofillValue, setPendingAutofillValue] = useState<
    string | null
  >(null);

  // Tracks a short window after Chrome triggers the autofill animation.
  const autofillActiveRef = useRef(false);
  const autofillTimerRef = useRef<number | undefined>(undefined);
  const startAutofillWindow = useCallback(() => {
    if (autofillTimerRef.current) {
      window.clearTimeout(autofillTimerRef.current);
    }
    autofillActiveRef.current = true;
    autofillTimerRef.current = window.setTimeout(() => {
      autofillActiveRef.current = false;
      autofillTimerRef.current = undefined;
    }, AUTOFILL_WINDOW_MS);
  }, []);
  const endAutofillWindow = useCallback(() => {
    if (autofillTimerRef.current) {
      window.clearTimeout(autofillTimerRef.current);
      autofillTimerRef.current = undefined;
    }
    autofillActiveRef.current = false;
  }, []);

  // Map "MI" or "Michigan" to an option
  const findStateMatch = useCallback(
    (raw: string): NamedOption | null => {
      if (!Array.isArray(autocompleteData) || name !== "state") return null;
      const options = autocompleteData as NamedOption[];
      const s = raw.trim();
      if (!s) return null;

      let match =
        options.find((o) => o.name.toLowerCase() === s.toLowerCase()) ?? null;

      if (!match && s.length === 2) {
        const full = US_STATE_ABBREVIATIONS[s.toUpperCase()];
        if (full) {
          match =
            options.find((o) => o.name.toLowerCase() === full.toLowerCase()) ??
            null;
        }
      }
      return match;
    },
    [autocompleteData, name]
  );

  const commitSelection = useCallback(
    (match: NamedOption) => {
      setValue(name, match as SelectOption, { shouldValidate: true });
      setInputText(match.name);
      additionalOnChange?.();
      setOpen(false);
      Promise.resolve().then(() => inputRef.current?.blur());
      endAutofillWindow();
      setPendingAutofillValue(null);
    },
    [additionalOnChange, endAutofillWindow, name, setValue]
  );

  // Try to read a committed value directly (kept as a best-effort; may be empty)
  const readCommittedAutofill = useCallback(
    (el: HTMLInputElement | HTMLTextAreaElement) =>
      new Promise<string | null>((resolve) => {
        let rafTries = 12;
        const tryRead = () => {
          const v = el.value;
          if (v) return resolve(v);
          if (rafTries-- > 0) requestAnimationFrame(tryRead);
          else resolve(null);
        };
        requestAnimationFrame(tryRead);
      }),
    []
  );

  const handleAutoFillAnimation = useCallback(
    async (e: React.AnimationEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.animationName !== "onAutoFillStart") return;
      if (name !== "state") return;

      // Mark that the next input events within this window are "autofill-driven"
      startAutofillWindow();

      // If Chrome already committed a value, use it now
      const el = e.currentTarget;
      const committed = await readCommittedAutofill(el);
      if (committed) {
        const match = findStateMatch(committed);
        if (match) {
          commitSelection(match);
        } else {
          // Stash the autofilled text — autocompleteData (states list)
          // probably hasn't loaded yet because getStatesThunk is gated
          // on the country resolving. The retry effect below will replay
          // findStateMatch as soon as the list arrives.
          setPendingAutofillValue(committed);
        }
      }
    },
    [
      commitSelection,
      findStateMatch,
      name,
      readCommittedAutofill,
      startAutofillWindow,
    ]
  );

  // If user is typing normally, we just update text and keep dropdown behavior.
  // If we're inside the autofill window, treat the first non-empty input as autofill and commit.
  const onInputEvent = useCallback(
    (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = ev.currentTarget.value;
      setInputText(v);
      setOpen(true);

      if (
        name === "state" &&
        autofillActiveRef.current &&
        v.trim().length >= 2
      ) {
        const match = findStateMatch(v);
        if (match) {
          commitSelection(match);
        } else {
          // Couldn't match yet — stash the autofilled value so the retry
          // effect can re-run findStateMatch when autocompleteData loads.
          // (Don't end the autofill window early; keep it stashed until
          // we either resolve it or the cleanup effect clears it.)
          setPendingAutofillValue(v);
        }
      }
    },
    [commitSelection, endAutofillWindow, findStateMatch, name]
  );

  const onChangeEvent = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = ev.currentTarget.value;
      setInputText(v);
      // no auto-commit here; typing should require explicit selection
    },
    []
  );

  const syncSelectedToText = useCallback((selected: SelectOptionOrNull) => {
    const nameVal = getName(selected ?? "");
    setInputText(nameVal);
  }, []);

  // Clean up window on unmount
  useEffect(() => {
    return () => endAutofillWindow();
  }, [endAutofillWindow]);

  // Retry pending state autofill once autocompleteData (the states list)
  // arrives. Browser autofill commonly fires before getStatesThunk resolves
  // because the autofill animation is synchronous on the address-level1
  // input but the states fetch is gated on the country selection.
  useEffect(() => {
    if (!pendingAutofillValue) return;
    if (!Array.isArray(autocompleteData) || autocompleteData.length === 0)
      return;
    if (name !== "state") return;
    const match = findStateMatch(pendingAutofillValue);
    if (match) {
      commitSelection(match);
    }
  }, [
    autocompleteData,
    pendingAutofillValue,
    findStateMatch,
    commitSelection,
    name,
  ]);

  return (
    <Controller
      control={useFormContext().control}
      name={name}
      shouldUnregister
      rules={{ validate: (v) => (required ? !!v : true) }}
      render={({
        field: { onChange, value, ref },
        fieldState: { invalid },
      }) => {
        // FIXME(sjd): this useEffect lives inside Controller's render prop,
        // which is a callback rather than a component body — a genuine
        // rules-of-hooks violation, surfaced when the react-hooks plugin was
        // finally enabled. It does not misbehave today only because Controller
        // invokes this render prop unconditionally on every render, so the hook
        // order stays stable by accident. The correct fix is to lift it to the
        // component body and read the field via useWatch({ control, name }).
        // Deliberately NOT done in the step-3 cart-race fix: this component
        // backs the step-1 pickup/destination pickers and the step-5
        // country/state pickers, so the change needs a real pass through those
        // flows rather than riding along with an unrelated fix.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          const nameVal = getName(value);
          setInputText(nameVal);
        }, [value]);

        const setFieldValue = (selected: SelectOptionOrNull) => {
          onChange(selected);
          syncSelectedToText(selected);
          additionalOnChange?.();
          if (name === "destinationLocation" || name === "pickupLocation") {
            setSelectedCar(null);
          }
        };

        return (
          <>
            <Label sx={{ marginLeft: "2px" }}>
              <T variant="inputTitle">{labelText}</T>
              {required && <RequiredStar />}
            </Label>

            <Autocomplete<SelectOption, false, false, false>
              options={(autocompleteData ?? []) as SelectOption[]}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, val) => option.name === val?.name}
              value={(value as SelectOptionOrNull) || null}
              inputValue={inputText}
              onInputChange={(_e, newInput, reason) => {
                setInputText(newInput ?? "");
                if (reason === "input") {
                  setOpen(true);
                }
              }}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              disableCloseOnSelect={false}
              PopperComponent={ShadowPopper}
              ListboxComponent={VirtualListbox}
              disableListWrap
              onChange={(_e, selected) =>
                setFieldValue(selected as SelectOptionOrNull)
              }
              renderInput={(params) => (
                <TextField
                  variant="outlined"
                  error={invalid}
                  {...params}
                  inputRef={(el) => {
                    ref(el);
                    inputRef.current = el;
                  }}
                  placeholder={placeholder}
                  sx={{
                    "@keyframes onAutoFillStart": { from: {}, to: {} },
                    "& input:-webkit-autofill": {
                      animationName: "onAutoFillStart",
                      animationDuration: "0.001s",
                      animationIterationCount: 1,
                    },
                  }}
                  inputProps={{
                    ...params.inputProps,
                    // Allow Chrome to consider this as address fields in autofill
                    autoComplete:
                      name === "state"
                        ? "address-level1"
                        : name === "country"
                          ? "country-name"
                          : params.inputProps?.autoComplete,
                    name:
                      name === "state"
                        ? "address-level1"
                        : name === "country"
                          ? "country-name"
                          : params.inputProps?.name,
                    // Detect start of autofill and open the window
                    onAnimationStart: handleAutoFillAnimation,
                    // During the window, the first input we see is treated as autofill
                    onInput: onInputEvent,
                    onChange: onChangeEvent,
                    onBlur: endAutofillWindow,
                  }}
                />
              )}
            />

            <Box>
              {invalid && <LabelError labelErrorText={labelErrorText} />}
            </Box>
          </>
        );
      }}
    />
  );
};

export default InputSelect;
